import { forwardRef, useEffect, useState, type KeyboardEventHandler } from "react";
import JSON5 from "json5";
import { Highlight } from "prism-react-renderer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import usePrismLanguages from "~/hooks/usePrismLanguages";
import { codeTheme } from "~/lib/codeTheme";
import { cn } from "~/lib/utils";
import type { ToolCallEntry } from "./useTurns";

interface ToolCallCardProps {
  entry: ToolCallEntry;
  tabIndex?: number;
  onFocus?: () => void;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
}

type TabValue = "input" | "output";

// Try JSON first, then JSON5 (covers Python-repr-style outputs like
// single-quoted strings). Returns the parsed object/array on success.
// Scalars and parse failures both return undefined so the caller falls
// back to plain-text rendering.
function tryParseJson(raw: string): object | undefined {
  if (!raw.trim()) return undefined;
  const attempts = [JSON.parse, JSON5.parse];
  for (const parse of attempts) {
    try {
      const parsed = parse(raw);
      if (parsed !== null && typeof parsed === "object") return parsed;
    } catch {
      // try next
    }
  }
  return undefined;
}

const TRIGGER_CLS = cn(
  "rounded-none border-b-2 border-transparent px-0 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground shadow-none",
  "data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
);

export const ToolCallCard = forwardRef<HTMLDivElement, ToolCallCardProps>(
  ({ entry, tabIndex, onFocus, onKeyDown }, ref) => {
    const pending = entry.result === undefined;

    // Default to Output when it's ready, otherwise Input. Once the user picks
    // a tab themselves we stop overriding their choice, but if they never
    // touched it we promote them to Output as soon as the result lands.
    const [tab, setTab] = useState<TabValue>(pending ? "input" : "output");
    const [touched, setTouched] = useState(false);

    useEffect(() => {
      if (!touched && !pending) setTab("output");
    }, [pending, touched]);

    const handleValueChange = (next: string) => {
      setTouched(true);
      setTab(next as TabValue);
    };

    return (
      <div
        ref={ref}
        tabIndex={tabIndex}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        className="text-xs focus:outline-none"
      >
        <Tabs value={tab} onValueChange={handleValueChange} className="gap-0">
          <TabsList className="h-auto justify-start gap-3 rounded-none border-b border-border bg-transparent px-3 py-0">
            <TabsTrigger value="input" className={TRIGGER_CLS}>
              Input
            </TabsTrigger>
            <TabsTrigger value="output" disabled={pending} className={TRIGGER_CLS}>
              Output
            </TabsTrigger>
          </TabsList>
          <TabsContent value="input">
            <Panel body={entry.args} placeholder="(no arguments)" />
          </TabsContent>
          <TabsContent value="output">
            <Panel body={entry.result ?? ""} placeholder="Waiting for result…" />
          </TabsContent>
        </Tabs>
      </div>
    );
  }
);
ToolCallCard.displayName = "ToolCallCard";

interface PanelProps {
  body: string;
  placeholder: string;
}

const PRE_CLS =
  "max-h-48 overflow-auto whitespace-pre-wrap break-words px-3 py-2 font-mono text-[11px] leading-relaxed";

function Panel({ body, placeholder }: PanelProps) {
  const { Prism } = usePrismLanguages(["json"]);
  const parsed = tryParseJson(body);

  if (parsed !== undefined) {
    const code = JSON.stringify(parsed, null, 2);
    return (
      <Highlight theme={codeTheme} code={code} language="json" prism={Prism}>
        {({ style, tokens, getLineProps, getTokenProps }) => (
          <pre className={PRE_CLS} style={style}>
            {tokens.map((line, i) => (
              <div key={i} {...getLineProps({ line })}>
                {line.map((token, key) => (
                  <span key={key} {...getTokenProps({ token })} />
                ))}
              </div>
            ))}
          </pre>
        )}
      </Highlight>
    );
  }

  return <pre className={PRE_CLS}>{body || placeholder}</pre>;
}
